import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';
import Role from 'features/role/domain/models/role';
import ReadTemporaryAccessResponse from 'features/temporary-access/domain/models/read-temporary-access-response';
import { ReadTemporaryAccessQuery } from 'features/temporary-access/domain/models/read-temporay-access-query';
import { CreateTemporaryUserCommand } from '../domain/models/create-temporary-user-command';
import { RevokeTemporaryChannelAccessCommand } from '../domain/models/revoke-temporary-channel-access-command';
import { RevokeTemporaryUserCommand } from '../domain/models/revoke-temporary-user-command';
import TemporaryUser from '../domain/models/temporary-user';
import { ValidateTemporaryUserPeriodCommand } from '../domain/models/validate-temporary-user-period-command';

const roleBaseUrl = '/api/v1/Role';
const baseUrl = '/api/v1/temporaryAccess';
const organizationBaseUrl = '/api/v1/OrganisationUnit';

enum RoleQueryPaths {
  ReadRolesForTemporaryAccess = 'ReadRolesForTemporaryAccess',
}

enum OrganisationUnitQueryPaths {
  ReadOrganisationUnitsForTemporaryAccess = 'ReadOrganisationUnitsForTemporaryAccess',
}

enum TemporaryAccessPaths {
  ReadTemporaryAccess = 'ReadTemporaryAccess',
  ReadTemporaryUser = 'ReadTemporaryUser',
  CreateTemporaryUser = 'CreateTemporaryUser',
  ValidateTemporaryUserPeriod = 'ValidateTemporaryUserPeriod',
  RevokeTemporaryUser = 'RevokeTemporaryUser',
  RevokeTemporaryChannelAccess = 'RevokeTemporaryChannelAccess',
}

const readRolesForTemporaryAccess = async (axiosInstance: AxiosInstance): Promise<Role[]> => {
  const response = await axiosInstance.get(`${roleBaseUrl}/${RoleQueryPaths.ReadRolesForTemporaryAccess}`);
  return response.data;
};

const readTemporaryAccess = async (axiosInstance: AxiosInstance, body: ReadTemporaryAccessQuery): Promise<ReadTemporaryAccessResponse> => {
  const response = await axiosInstance.post(`${baseUrl}/${TemporaryAccessPaths.ReadTemporaryAccess}`, body);
  return response.data;
};

const readTemporaryUser = async (axiosInstance: AxiosInstance, id: string): Promise<TemporaryUser> => {
  const response = await axiosInstance.get(`${baseUrl}/${TemporaryAccessPaths.ReadTemporaryUser}/${id}`);
  return response.data;
};

const createTemporaryUser = async (axiosInstance: AxiosInstance, body: CreateTemporaryUserCommand): Promise<void> => {
  await axiosInstance.post(`${baseUrl}/${TemporaryAccessPaths.CreateTemporaryUser}`, body);
};

export const validateTemporaryUserPeriod = async (
  axiosInstance: AxiosInstance,
  body: ValidateTemporaryUserPeriodCommand,
): Promise<boolean> => {
  try {
    await axiosInstance.post(`${baseUrl}/${TemporaryAccessPaths.ValidateTemporaryUserPeriod}`, body);
    return true;
  } catch {
    return false;
  }
};

const revokeTemporaryUser = async (axiosInstance: AxiosInstance, body: RevokeTemporaryUserCommand): Promise<void> => {
  await axiosInstance.put(`${baseUrl}/${TemporaryAccessPaths.RevokeTemporaryUser}`, body);
};

const revokeTemporaryChannelAccess = async (axiosInstance: AxiosInstance, body: RevokeTemporaryChannelAccessCommand): Promise<void> => {
  await axiosInstance.put(`${baseUrl}/${TemporaryAccessPaths.RevokeTemporaryChannelAccess}`, body);
};

const readOrganizationUnitsForTemporaryAccess = async (axiosInstance: AxiosInstance): Promise<OrganisationUnit[]> => {
  const response = await axiosInstance.get(`${organizationBaseUrl}/${OrganisationUnitQueryPaths.ReadOrganisationUnitsForTemporaryAccess}`);
  return response.data;
};

export const useLazyReadTemporaryAccess = (body: ReadTemporaryAccessQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [TemporaryAccessPaths.ReadTemporaryAccess],
    queryFn: () => readTemporaryAccess(authenticatedInstance, body),
    enabled: false,
  });
};

export const useLazyReadTemporaryUser = (id: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [TemporaryAccessPaths.ReadTemporaryUser, id],
    queryFn: () => readTemporaryUser(authenticatedInstance, id),
    enabled: false,
  });
};

export const useCreateTemporaryUser = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [TemporaryAccessPaths.CreateTemporaryUser],
    mutationFn: (body: CreateTemporaryUserCommand) => createTemporaryUser(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TemporaryAccessPaths.ReadTemporaryAccess] });
    },
  });
};

export const useRevokeTemporaryUser = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [TemporaryAccessPaths.RevokeTemporaryUser],
    mutationFn: (body: RevokeTemporaryUserCommand) => revokeTemporaryUser(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TemporaryAccessPaths.ReadTemporaryAccess] });
    },
  });
};

export const useRevokeTemporaryChannelAccess = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [TemporaryAccessPaths.RevokeTemporaryChannelAccess],
    mutationFn: (body: RevokeTemporaryChannelAccessCommand) => revokeTemporaryChannelAccess(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TemporaryAccessPaths.ReadTemporaryAccess] });
    },
  });
};

export const useLazyReadRolesForTemporaryAccess = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [RoleQueryPaths.ReadRolesForTemporaryAccess],
    queryFn: () => readRolesForTemporaryAccess(authenticatedInstance),
    enabled: false,
  });
};

export const useLazyReadOrganizationUnitsForTemporaryAccess = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.ReadOrganisationUnitsForTemporaryAccess],
    queryFn: () => readOrganizationUnitsForTemporaryAccess(authenticatedInstance),
    enabled: false,
  });
};
